class MultiAddUpsell extends HTMLElement {
  constructor() {
    super();

    this.initialized = false;
    this.modalStates = [];
    this.productData = null;
    this.pendingSubmitState = null;
    this.currentCartTotal = 0;

    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleModalTriggerClick = this.handleModalTriggerClick.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleCartAdded = this.handleCartAdded.bind(this);
    this.handleCartError = this.handleCartError.bind(this);
    this.handleCartChange = this.handleCartChange.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.form = document.getElementById(this.dataset.form);
    if (!this.form) return;

    this.productData = this.getProductData();
    this.buildModalForms();
    this.updateUpsellImages();
    document.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('submit', this.handleFormSubmit);
    document.addEventListener('cart:added', this.handleCartAdded);
    document.addEventListener('cart:error', this.handleCartError);
    document.addEventListener('cart:loaded', this.handleCartChange);
    document.addEventListener('cart:updated', this.handleCartChange);
    document.addEventListener('cart:added', this.handleCartChange);
    document.addEventListener('cart:cleared', this.handleCartChange);
    this.addEventListener('click', this.handleModalTriggerClick);
    this.syncCurrentCartTotal();

    this.initialized = true;
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.handleVariantChange);
    document.removeEventListener('submit', this.handleFormSubmit);
    document.removeEventListener('cart:added', this.handleCartAdded);
    document.removeEventListener('cart:error', this.handleCartError);
    document.removeEventListener('cart:loaded', this.handleCartChange);
    document.removeEventListener('cart:updated', this.handleCartChange);
    document.removeEventListener('cart:added', this.handleCartChange);
    document.removeEventListener('cart:cleared', this.handleCartChange);
    this.removeEventListener('click', this.handleModalTriggerClick);
  }

  getProductData() {
    const productJson = this.form?.querySelector('script[data-product-json]');
    if (!productJson?.textContent) return null;

    try {
      return JSON.parse(productJson.textContent);
    } catch (error) {
      console.warn('MultiAddUpsell: Failed to parse product JSON', error);
      return null;
    }
  }

  buildModalForms() {
    const modals = Array.from(this.querySelectorAll('modal-dialog'));

    modals.forEach((modal, index) => {
      const variants = Array.from(modal.querySelectorAll('.upsell__variant[data-variant-id]'));
      if (!variants.length) return;
      const footer = modal.querySelector('[data-multi-upsell-footer]');
      if (!footer) return;
      const modalForm = modal.querySelector('form[data-multi-upsell-form]');
      if (!modalForm) return;

      const submitButton = footer.querySelector('[data-upsell-submit]');
      if (submitButton && !submitButton.dataset.upsellFormIndex) {
        submitButton.dataset.upsellFormIndex = String(index);
      }

      const state = {
        modal,
        form: modalForm,
        footer,
        variants,
        items: []
      };

      this.setupVariantEvents(state);
      this.refreshState(state);
      this.modalStates.push(state);
    });
  }

  setupVariantEvents(state) {
    state.variants.forEach((variant) => {
      if (variant.classList.contains('selected') && !variant.classList.contains('select')) {
        variant.classList.add('select');
      }

      variant.setAttribute('role', 'button');
      variant.setAttribute('tabindex', '0');
      variant.setAttribute('aria-pressed', variant.classList.contains('select') ? 'true' : 'false');

      variant.addEventListener('click', () => {
        this.toggleVariant(state, variant);
      });

      variant.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        this.toggleVariant(state, variant);
      });
    });
  }

  toggleVariant(state, variant) {
    variant.classList.toggle('select');
    variant.classList.toggle('selected');
    variant.setAttribute('aria-pressed', variant.classList.contains('select') ? 'true' : 'false');

    this.refreshState(state);
  }

  refreshState(state) {
    state.items = this.getItemsForState(state);
    this.syncUpsellForm(state);
    this.updateTotal(state);
    this.updateSubmitState(state);
  }

  getItemsForState(state) {
    const items = [];
    const baseVariantId = this.getBaseVariantId();

    if (baseVariantId) {
      items.push(String(baseVariantId));
    }

    state.variants.forEach((variant) => {
      if (!variant.classList.contains('select')) return;

      const variantId = variant.dataset.variantId?.trim();
      if (!variantId || items.includes(variantId)) return;

      items.push(variantId);
    });

    return items;
  }

  syncUpsellForm(state) {
    this.refreshInputs(state?.form, state?.items || []);
  }

  isStateOpen(state) {
    return state?.modal?.dataset.open === 'true';
  }

  refreshInputs(form, items = []) {
    if (!form) return;

    form.querySelectorAll('[name="id"], [name="quantity"], input[name^="items["]').forEach((input) => {
      input.remove();
    });

    const inputContainer = form.querySelector('[data-multi-upsell-inputs]') || form;

    items.forEach((variantId, index) => {
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = `items[${index}][id]`;
      idInput.value = String(variantId);

      const quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = `items[${index}][quantity]`;
      quantityInput.value = '1';

      inputContainer.appendChild(idInput);
      inputContainer.appendChild(quantityInput);
    });
  }

  updateTotal(state) {
    const totalTarget = state.footer.querySelector('[data-upsell-total-price]');
    const freeShippingTarget = state.footer.querySelector('[data-upsell-free-shipping-copy]');
    if (!totalTarget && !freeShippingTarget) return;

    const basePrice = this.getBaseVariantPrice();
    const selectedUpsellPrice = state.variants.reduce((sum, variant) => {
      if (!variant.classList.contains('select')) return sum;
      return sum + this.parsePrice(variant.dataset.price);
    }, 0);
    const totalPrice = basePrice + selectedUpsellPrice;
    const freeShippingProgressTotal = this.currentCartTotal + totalPrice;

    console.log("Current Total: " + this.currentCartTotal + " Band total: " + totalPrice)
    if (totalTarget) {
      totalTarget.textContent = this.formatMoney(totalPrice);
    }

    if (freeShippingTarget) {
      const freeShippingTargetAmount = state.footer.dataset.freeShippingTarget?.trim();

      if (!freeShippingTargetAmount) {
        freeShippingTarget.textContent = '';
        return;
      }

      const freeShippingThreshold = this.parsePrice(freeShippingTargetAmount) * 100;
      const remainingAmount = freeShippingThreshold - freeShippingProgressTotal;

      freeShippingTarget.textContent = remainingAmount <= 0
        ? 'Free Shipping unlocked'
        : `${this.formatMoney(remainingAmount)} left to unlock free shipping`;
    }
  }

  updateSubmitState(state) {
    const submitButton = state.footer.querySelector('[data-upsell-submit]');
    if (!submitButton) return;

    submitButton.disabled = state.items.length <= 1;
  }

  handleVariantChange() {
    this.updateUpsellImages();
    this.modalStates.forEach((state) => {
      this.refreshState(state);
    });
  }

  handleModalTriggerClick(event) {
    const trigger = event.target.closest('.upsell-card [data-modal-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const modalId = trigger.getAttribute('data-modal-trigger');
    if (!modalId) return;

    const state = this.modalStates.find((item) => item.modal.dataset.modalId === modalId);
    if (!state) return;

    this.syncCurrentCartTotal();
    this.refreshState(state);
  }

  handleFormSubmit(event) {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;

    const state = this.modalStates.find((item) => item.form === form);
    if (!state) return;

    this.pendingSubmitState = state;
  }

  handleCartAdded() {
    if (!this.pendingSubmitState) return;

    if (typeof this.pendingSubmitState.modal?.close === 'function') {
      this.pendingSubmitState.modal.close();
    } else {
      this.pendingSubmitState.modal?.setAttribute('hidden', '');
    }

    this.pendingSubmitState = null;
  }

  handleCartError() {
    this.pendingSubmitState = null;
  }

  handleCartChange(event) {
    this.currentCartTotal = this.getCartTotal(event.detail?.cart);
    this.modalStates.forEach((state) => {
      this.refreshState(state);
    });
  }

  syncCurrentCartTotal() {
    const cart = window.cartManager?.cart;

    if (cart) {
      this.currentCartTotal = this.getCartTotal(cart);
    }

    if (typeof window.cartManager?.getCart !== 'function') return;

    window.cartManager.getCart()
      .then((freshCart) => {
        this.currentCartTotal = this.getCartTotal(freshCart);
        this.modalStates.forEach((state) => {
          this.refreshState(state);
        });
      })
      .catch((error) => {
        console.warn('MultiAddUpsell: Failed to refresh cart total', error);
      });
  }

  getCartTotal(cart) {
    if (!cart) return 0;

    if (cart.total_price != null) {
      return this.parsePrice(cart.total_price);
    }

    return this.parsePrice(cart.items_subtotal_price);
  }

  getBaseVariantId() {
    const variantManagerId = Number(window.variantManager?.getCurrentVariantId?.());
    if (variantManagerId) return variantManagerId;

    const idInput = this.form?.querySelector('[name="id"], [data-variant-id], [data-variant-input]');
    const inputId = Number(idInput?.value);

    return Number.isFinite(inputId) && inputId > 0 ? inputId : null;
  }

  getBaseVariantPrice() {
    const currentVariant = window.variantManager?.getCurrentVariant?.();
    if (currentVariant?.price != null) {
      return this.parsePrice(currentVariant.price);
    }

    const baseVariantId = this.getBaseVariantId();
    if (!baseVariantId || !Array.isArray(this.productData?.variants)) return 0;

    const matchingVariant = this.productData.variants.find((variant) => Number(variant.id) === Number(baseVariantId));
    return this.parsePrice(matchingVariant?.price);
  }

  getCurrentColorTheme() {
    const currentVariant = window.variantManager?.getCurrentVariant?.();
    const variantLabel = [
      currentVariant?.title,
      currentVariant?.option1,
      currentVariant?.option2,
      currentVariant?.option3
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (variantLabel.includes('black')) return 'black';
    if (variantLabel.includes('rose gold') || variantLabel.includes('rosegold')) return 'rosegold';

    return 'black';
  }

  updateUpsellImages() {
    const colorTheme = this.getCurrentColorTheme();
    const imageAttr = colorTheme === 'rosegold' ? 'imageRosegold' : 'imageBlack';

    this.querySelectorAll('.upsell-card, .upsell__variant').forEach((item) => {
      const nextImage = item.dataset[imageAttr];
      if (!nextImage) return;

      const image = item.querySelector('img');
      if (!image) return;

      image.src = nextImage;
      image.srcset = nextImage;
    });
  }

  parsePrice(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || window.Shopify?.shop?.currency || 'USD';
    const locale = document.documentElement.getAttribute('lang') || navigator.language || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format((cents || 0) / 100);
    } catch (error) {
      return `$${((cents || 0) / 100).toFixed(2)}`;
    }
  }
}

function initMultiAddUpsell() {
  if (!window.customElements.get('product-upsell')) {
    window.customElements.define('product-upsell', MultiAddUpsell);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMultiAddUpsell);
} else {
  initMultiAddUpsell();
}
